/**
 * ZoneFloorPicker — V14.98 : renseigner le sol dédié d'une pièce en zone
 * ouverte sans quitter l'éditeur (copie d'un matériau « sols » de la
 * Matériauthèque, ou saisie libre).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

export const useZoneFloors = (projectId, lotId, enabled) => {
  const [data, setData] = useState({ options: [], slots: [] });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!enabled || !projectId || !lotId) return;
    axios.get(`${API}/projects/${projectId}/plan-source/zone-floors`, { ...opts, params: { lot_id: lotId } })
      .then((r) => setData(r.data || { options: [], slots: [] }))
      .catch(() => {});
  }, [projectId, lotId, enabled, tick]);
  return { ...data, refresh: () => setTick((t) => t + 1), patchSlot: (slot) => setData((d) => ({
    ...d, slots: [...d.slots.filter((s) => s.room_id !== slot.room_id), slot],
  })) };
};

export const ZoneFloorPicker = ({ projectId, lotId, room, options, slot, onSaved }) => {
  const [mode, setMode] = useState('pick'); // pick | free
  const [free, setFree] = useState('');
  const [busy, setBusy] = useState(false);
  const filled = !!slot?.filled;

  const save = async (body) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/projects/${projectId}/plan-source/zone-floor`, { room_id: room.id, ...body }, { ...opts, params: { lot_id: lotId } });
      onSaved?.(r.data);
      toast.success(`Sol de « ${room.label} » : ${r.data?.name}`, { description: 'Repris dans la Matériauthèque et le prompt Texturer.' });
      setMode('pick'); setFree('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`zone-floor-picker-${room.id}`}>
      {filled && mode === 'pick' && (
        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300/90" data-testid={`zone-floor-current-${room.id}`}>
          <Check className="w-3 h-3" /> Sol : <strong className="text-emerald-200">{slot.name}</strong>{slot.ref ? ` · ${slot.ref}` : ''}
        </span>
      )}
      {mode === 'pick' ? (
        <>
          <select
            value=""
            disabled={busy}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '__free__') setMode('free');
              else if (v) save({ source_material_id: v });
            }}
            data-testid={`zone-floor-select-${room.id}`}
            className="h-6 text-[10px] px-1.5 rounded bg-white/[0.06] border border-white/10 text-white focus:outline-none focus:border-emerald-400/60 max-w-[220px]"
          >
            <option value="" className="bg-[#0F1115]">{filled ? 'Changer le sol…' : 'Choisir le sol de cette zone…'}</option>
            {options.map((o) => (
              <option key={o.material_id} value={o.material_id} className="bg-[#0F1115]">
                {o.name}{o.ref ? ` · ${o.ref}` : ''}
              </option>
            ))}
            <option value="__free__" className="bg-[#0F1115]">✎ Autre (saisir un matériau)…</option>
          </select>
          {busy && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
        </>
      ) : (
        <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); if (free.trim()) save({ name: free }); }}>
          <Pencil className="w-3 h-3 text-gray-400" />
          <input
            autoFocus value={free} onChange={(e) => setFree(e.target.value)} disabled={busy}
            placeholder="ex. Grès cérame 60×60 gris"
            data-testid={`zone-floor-free-${room.id}`}
            className="h-6 text-[10px] px-1.5 rounded bg-white/[0.06] border border-white/10 text-white focus:outline-none focus:border-emerald-400/60 w-[190px]"
          />
          <button type="submit" disabled={busy || !free.trim()} data-testid={`zone-floor-free-save-${room.id}`}
                  className="h-6 px-2 rounded text-[10px] font-semibold disabled:opacity-40" style={{ background: 'rgba(34,197,94,0.25)', color: '#bbf7d0' }}>
            OK
          </button>
          <button type="button" onClick={() => setMode('pick')} className="h-6 px-1.5 text-[10px] text-gray-400 hover:text-white">Annuler</button>
        </form>
      )}
    </div>
  );
};

export default ZoneFloorPicker;
